import { Fragment, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { AuditPage, UserSummary } from '@agency/contracts';
import { Badge, Button, EmptyState, SelectField, Skeleton, TextField, scrollRegion } from '@agency/ui';
import { ApiClientError, get } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { fmtDateSeconds } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';

const ACTION_GROUPS: { prefix: string; label: string }[] = [
  { prefix: 'auth.', label: 'Sign-in and sign-out' },
  { prefix: 'mfa.', label: 'Two-step verification' },
  { prefix: 'session.', label: 'Sessions' },
  { prefix: 'user.', label: 'Users and passwords' },
  { prefix: 'invite.', label: 'Invitation acceptance' },
  { prefix: 'role.', label: 'Roles and permissions' },
  { prefix: 'flag.', label: 'Feature flags' },
  { prefix: 'landing.', label: 'Landing pages' },
  { prefix: 'lead.', label: 'Leads' },
];
const tone = (a: string) => (a.includes('failed') ? 'error' : /deleted|revoked|disabled/.test(a) ? 'warning' : /created|enabled|accepted|login$/.test(a) ? 'success' : 'neutral') as 'error' | 'warning' | 'success' | 'neutral';
const toIso = (local: string) => (local ? new Date(local).toISOString() : undefined);

export function AuditPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const filters = { action: params.get('action') ?? '', actorId: params.get('actorId') ?? '', from: params.get('from') ?? '', to: params.get('to') ?? '' };
  const set = (k: string, v: string) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });
  const [open, setOpen] = useState<string | null>(null);

  const users = useQuery<UserSummary[], ApiClientError>({ queryKey: ['team'], queryFn: () => get('/users'), enabled: can('users.read') });
  const q = useInfiniteQuery<AuditPage, ApiClientError>({
    queryKey: ['audit', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const s = new URLSearchParams({ limit: '50' });
      if (filters.action) s.set('action', filters.action);
      if (filters.actorId) s.set('actorId', filters.actorId);
      if (filters.from) s.set('from', toIso(filters.from)!);
      if (filters.to) s.set('to', toIso(filters.to)!);
      if (pageParam) s.set('cursor', pageParam as string);
      return get(`/audit?${s}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = Object.values(filters).some(Boolean);

  return (
    <>
      <div className="pagehead">
        <div><h1>Audit log</h1><p>Security and high-impact actions, newest first. Entries are append-only: nobody can edit or delete them, including administrators.</p></div>
      </div>
      <form className="toolbar" role="search" aria-label="Filter the audit log" onSubmit={(e) => e.preventDefault()}>
        <SelectField label="Action" value={filters.action} onChange={(e) => set('action', e.target.value)}>
          <option value="">All actions</option>
          {ACTION_GROUPS.map((g) => <option key={g.prefix} value={g.prefix}>{g.label}</option>)}
        </SelectField>
        {can('users.read') ? (
          <SelectField label="Person" value={filters.actorId} onChange={(e) => set('actorId', e.target.value)}>
            <option value="">Anyone</option>
            {(users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </SelectField>
        ) : null}
        <TextField label="From" type="datetime-local" value={filters.from} onChange={(e) => set('from', e.target.value)} />
        <TextField label="To" type="datetime-local" value={filters.to} onChange={(e) => set('to', e.target.value)} />
        {filtered ? <Button onClick={() => setParams({}, { replace: true })}>Clear filters</Button> : null}
      </form>

      {q.isLoading ? <Skeleton width="100%" height={260} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : rows.length === 0 ? (
        <EmptyState title={filtered ? 'No entries match these filters' : 'Nothing recorded yet'} icon="activity" action={filtered ? <Button onClick={() => setParams({}, { replace: true })}>Clear filters</Button> : undefined}>
          {filtered ? 'Widen the date range or choose another action.' : 'Sign-ins, role changes and other sensitive actions appear here as they happen.'}
        </EmptyState>
      ) : (
        <>
          <div className="ag-table-wrap" {...scrollRegion('Audit entries')}>
            <table className="ag-table">
              <caption className="ag-visually-hidden">Audit entries, newest first</caption>
              <thead><tr><th scope="col">When</th><th scope="col">Who</th><th scope="col">Action</th><th scope="col">Target</th><th scope="col"><span className="ag-visually-hidden">Details</span></th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <Fragment key={r.id}>
                    <tr>
                      <td className="ag-mono" style={{ whiteSpace: 'nowrap' }}>{fmtDateSeconds(r.createdAt)}</td>
                      <td>{r.actorName ?? <span className="ag-muted">{r.actorId ? 'Removed user' : 'System / signed out'}</span>}</td>
                      <td><Badge tone={tone(r.action)}>{r.action}</Badge></td>
                      <td><span className="ag-cell-2"><span>{r.entityType}</span><span className="ag-mono">{r.entityId.slice(0, 12)}</span></span></td>
                      <td style={{ textAlign: 'right' }}><Button size="sm" variant="ghost" aria-expanded={open === r.id} aria-controls={`audit-${r.id}`} onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? 'Hide' : 'Details'}</Button></td>
                    </tr>
                    {open === r.id ? (
                      <tr id={`audit-${r.id}`}>
                        <td colSpan={5}>
                          <dl className="facts">
                            <dt>Entity</dt><dd className="ag-mono">{r.entityType} {r.entityId}</dd>
                            {r.ip ? <><dt>Network address</dt><dd className="ag-mono">{r.ip}</dd></> : null}
                            {r.userAgent ? <><dt>Device</dt><dd>{r.userAgent}</dd></> : null}
                            {r.requestId ? <><dt>Request</dt><dd className="ag-mono">{r.requestId}</dd></> : null}
                            {r.before != null ? <><dt>Before</dt><dd><pre className="mono-block">{JSON.stringify(r.before, null, 2)}</pre></dd></> : null}
                            {r.after != null ? <><dt>After</dt><dd><pre className="mono-block">{JSON.stringify(r.after, null, 2)}</pre></dd></> : null}
                            {r.metadata != null ? <><dt>Details</dt><dd><pre className="mono-block">{JSON.stringify(r.metadata, null, 2)}</pre></dd></> : null}
                          </dl>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pager">
            <span className="ag-muted" role="status">{rows.length} entr{rows.length === 1 ? 'y' : 'ies'} loaded{q.hasNextPage ? '' : ' · end of log'}</span>
            {q.hasNextPage ? <Button loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>Load older entries</Button> : null}
          </div>
        </>
      )}
    </>
  );
}
